import React, { useCallback, useState } from 'react';
import classNames from "classnames";
import { InlineLoader } from "./../Loaders";
import styles from "./FormFieldInputNew.module.scss";
import { FieldError, Button, Span } from "./../../";
import { ShowIcon, ShowPasswordIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import _ from "lodash";

export const LabelChildren = ({ isOptional, labelChildren, disabled }) => {
    return <div className={styles["label-children"]}>
        {isOptional && <Span italic color={disabled ? "text-disabled" : undefined}>Optional</Span>}
        {labelChildren}
    </div>
};

export const Error = ({ showError, errorClassName, errorStyle, errorMessage, errorType = "error" }) => {
    if (showError) {
        return <FieldError className={classNames(styles.error, errorClassName)} type={errorType} style={errorStyle} message={errorMessage}/>
    } else {
        return "";
    }
};

export const Formidable = React.forwardRef((props, ref) => {
    const {
        Component,
        form,
        name,
        onChange: _onChange,
        value: _value,
        errorMessage: _errorMessage,
        showError: _showError,
        onBlur: _onBlur,
        useHandleFormChangeWrapper
    } = props;
    const [wasFocused, setWasFocused] = useState(false);
    const { onChange, value, errorMessage, showError } = formHandler({
        _onChange,
        _value,
        _errorMessage,
        _showError,
        form,
        name,
        wasFocused,
        useHandleFormChangeWrapper
    });

    const onBlur = useCallback((e) => {
        setWasFocused(true);
        _onBlur && _onBlur(e);
    }, [setWasFocused, _onBlur]);

    return <Component onBlur={onBlur} {...props} form={undefined} onChange={onChange} value={value}
                      errorMessage={errorMessage} showError={showError} ref={ref}/>
});

export const formHandler = ({
                                _onChange,
                                _value,
                                _showError,
                                _errorMessage,
                                form,
                                name,
                                useHandleFormChangeWrapper,
                                wasFocused
                            }) => {
    let onChange = _onChange;
    let value = _value;
    let errorMessage = _errorMessage;
    let showError = _showError;
    if (form && name) {
        value = _value || form.formState[name];
        onChange = _onChange || (useHandleFormChangeWrapper ? form.wrappedHandleFormChange(name) : form.handleFormChange);
        errorMessage = _errorMessage || form.formErrors[name];
        if (_.isUndefined(_showError)) {
            showError = (form.submissionAttempted || (form.dirty[name] && wasFocused)) && form.formErrors[name]
        }
    }

    return { onChange, value, errorMessage, showError };
};

const CharCounter = ({ length, maxChars, disabled }) => {
    return <div className={styles["char-counter"]}>
        <Span color={disabled ? "text-disabled" : undefined}>{length}/{maxChars}</Span>
    </div>
}

const Base = React.forwardRef((props, ref) => {
    const {
        Component,
        isOptional, label, labelChildren, useLabelMargin, bottomMargin = true,
        type, name, onChange, value, errorMessage, showError, errorType, disabled,
        maxChars, charCount,
        inputChildren, inputButtons, inputChildrenClassName, inputChildrenStyle,
        className, style, inputStyle, labelStyle, inputClassName, labelClassName, errorClassName, errorStyle,
        pending,
        ...rest
    } = props;

    const handleInput = (e) => {
        if (maxChars && (e.target.value.length > maxChars)) {
            return;
        }
        onChange(e);
    };

    return (
        <div
            className={classNames(styles["input-container"], { [styles["margin-bottom"]]: bottomMargin }, className)}
            style={style}>
            <LabelChildren disabled={disabled} isOptional={isOptional} labelChildren={labelChildren}/>
            <label className={classNames({ [styles["disabled"]]: disabled, [styles["invalid"]]: showError })}>
                {label && <span className={classNames(labelClassName, styles["label"])} style={labelStyle}>{label}</span>}
                {useLabelMargin && <span>&nbsp;</span>}
                <div className={styles['input-wrapper']}>
                    <Component type={type ? type : "text"} {...rest} name={name} value={value} ref={ref}
                               onChange={handleInput} className={classNames(inputClassName, { [styles['has-input-buttons']]: !!inputButtons })} style={inputStyle}
                               disabled={disabled}/>
                    {pending && !inputButtons && <InlineLoader className={styles["inline-loader"]}/>}
                    {inputChildren && !inputButtons && <div className={classNames(styles["input-children"], inputChildrenClassName)} style={inputChildrenStyle}>{inputChildren}</div>}
                    {inputButtons && <div className={styles['input-buttons']}>
                        {pending && <InlineLoader className={classNames(styles["inline-loader-inside"])}/>}
                        {inputChildren && <div className={classNames(styles["input-children-inside"], inputChildrenClassName)} style={inputChildrenStyle}>{inputChildren}</div>}
                        {inputButtons}
                    </div>}
                </div>
            </label>
            {charCount && <CharCounter length={value?.length || 0} maxChars={maxChars} disabled={disabled}/>}
            <Error showError={showError} errorClassName={errorClassName} errorStyle={errorStyle} errorType={errorType} errorMessage={errorMessage}/>
        </div>
    );
});

const FormFieldInline_ = React.memo(React.forwardRef((props, ref) => <Base {...props} Component="input" ref={ref}/>));

export const FormFieldInline = React.forwardRef((props, ref) => {
    return <Formidable {...props} className={classNames(props.className, styles['inline-input'])} ref={ref} Component={FormFieldInline_}/>
});

const FormFieldTextArea_ = React.memo(React.forwardRef((props, ref) => <Base {...props} Component="textarea" ref={ref}/>));

export const FormFieldTextArea = React.forwardRef((props, ref) => {
    return <Formidable {...props} ref={ref} Component={FormFieldTextArea_}/>
});

const FormFieldInput_ = React.memo(React.forwardRef((props, ref) => <Base {...props} Component="input" ref={ref}/>));

const FormFieldPassword = React.memo(React.forwardRef((props, ref) => {
    const [showPassword, setShowPassword] = useState(false)

    return <Base {...props}
                 type={showPassword ? "text" : "password"}
                 autoComplete="new-password"
                 Component="input"
                 inputChildrenClassName={styles['show-password-children']}
                 inputChildren={<Button tabIndex={-1} variant="icon" color="secondary" disabled={props.disabled} onClick={(e) => {
                     setShowPassword(!showPassword)
                     e.preventDefault();
                     e.stopPropagation();
                 }}>{showPassword ? <ShowPasswordIcon className={styles.hide}/> : <ShowIcon className={styles.show}/>}</Button>}
                 ref={ref}
    />
}));

export default React.forwardRef((props, ref) => {
    return <Formidable {...props} ref={ref} Component={props.type === "password" ? FormFieldPassword : FormFieldInput_}/>
});
